import React, { useState } from "react";

import { Alert } from "react-bootstrap";
import { CircularProgress } from "@mui/material";
import { Steps } from "rsuite";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/react";

import useIpAddress from "utils/useIpAddress";
import Register from "components/Register";
import GameService from "services/GameService";
import SubmissionService from "services/SubmissionService";
import InfoGameView from "./info";
import PointsGameView from "./points";
import DonutGameView from "./donut";
import SortGameView from "./sort";
import Realestate from "./realestate";
import useSessionId from "./useSessionId";
import Questions from "./questions";
import Timestamp from "utils/Timestamp";

/// The initial game landing page which loads the remote game config
/// displaying the appropriate game type while also providing context
const Play = () => {
  const sessionId = useSessionId();
  const ipAddress = useIpAddress();
  const navigate = useNavigate();
  const { gameId } = useParams();

  const [game, setGame] = useState(null);
  const [scores, setScores] = useState([]);
  const [pollResults, setPollResults] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [gameStartTimestamp, setGameStartTimestamp] = useState(null);
  const [roundStartTimestamps, setRoundStartTimestamps] = useState({});

  // derived state variables
  const isLoading = game === null;
  const name = game?.name ?? "Game";
  const rounds = game?.rounds?.length ?? 1;
  const currentRound = scores.length;
  const progressText = `${currentRound + 1} of ${rounds} rounds`;

  const userAnsweredPreQuestions =
    game?.questions?.pre?.length > 0 ? Object.keys(pollResults?.pre ?? {}).length > 0 : true;
  const userAnsweredPostQuestions =
    game?.questions?.post?.length > 0 ? Object.keys(pollResults?.post ?? {}).length > 0 : true;

  // Game derived vars
  const currentGame = game?.rounds[currentRound] ?? null;
  const currentMode = currentGame?.mode || currentGame?.type;

  const completedGamePlay = currentRound >= rounds;
  const userCompletedPolls = userAnsweredPreQuestions && userAnsweredPostQuestions;
  // Track game start timestamp when game begins (after pre-questions or when first round starts)
  React.useEffect(() => {
    if (game && userAnsweredPreQuestions && !gameStartTimestamp && currentRound === 0) {
      setGameStartTimestamp(Timestamp());
    }
  }, [game, userAnsweredPreQuestions, gameStartTimestamp, currentRound]);

  // Track round start timestamp when a new round begins
  React.useEffect(() => {
    if (game && userAnsweredPreQuestions && !completedGamePlay && currentRound < rounds) {
      setRoundStartTimestamps(prev => {
        // Only set timestamp if it doesn't already exist for this round
        if (prev[currentRound]) {
          return prev;
        }
        return {
          ...prev,
          [currentRound]: Timestamp()
        };
      });
    }
  }, [game, userAnsweredPreQuestions, completedGamePlay, currentRound, rounds]);

  // Handle state changes
  React.useEffect(() => {
    function loadGame() {
      if (game == null) {
        GameService.getGame(gameId).then(setGame);
      }
    }
    loadGame();

    const handleRoundSubmitted = () => {
      // Prevent multiple submissions
      if (isSubmitting) {
        return;
      }

      // Ensure game is loaded before attempting submission
      if (!game) {
        return;
      }

      // Check if all conditions are met for submission
      // Ensure we have at least one round and scores match rounds
      if (rounds > 0 && currentRound >= rounds && userCompletedPolls && scores.length > 0) {
        setIsSubmitting(true);
        
        // Determine the game ID to use
        const submissionGameId = game?.id ?? gameId;
        
        // Validate game ID before submission
        if (!submissionGameId) {
          const errorMessage = "Cannot submit: Game ID is missing";
          console.error(errorMessage, { game, gameId, gameName: game?.name });
          Sentry.captureException(new Error(errorMessage), {
            tags: { errorType: "missing_game_id" },
            extra: { game, gameId, gameName: game?.name },
          });
          setSubmissionError("Unable to save submission: Game configuration error. Please refresh and try again.");
          setIsSubmitting(false);
          return;
        }
        
        // Add a new document with a generated id.
        SubmissionService.addSubmission(submissionGameId, {
          rounds: scores,
          gameStartTimestamp: gameStartTimestamp,
          gameEndTimestamp: Timestamp(),
          ...(ipAddress && { ipAddress: ipAddress }),
          ...(pollResults && { profile: pollResults }),
          ...(sessionId && { sessionId: sessionId }),
        }).then((result) => {
          
          if (!result || !result.id) {
            const errorMessage = "Submission failed: result is missing id";
            console.error(errorMessage, result);
            
            // Capture in Sentry with context
            Sentry.captureException(new Error(errorMessage), {
              tags: {
                errorType: "submission_missing_id",
                gameId: game?.id ?? gameId,
                gameName: game?.name,
              },
              extra: {
                result,
                resultType: typeof result,
                resultStringified: JSON.stringify(result),
                scoresLength: scores.length,
                gameName: game?.name,
                submissionGameId,
                gameHasId: !!game?.id,
                routeGameId: gameId,
              },
            });
            
            setSubmissionError("Failed to save your submission. Please try again or contact support.");
            setIsSubmitting(false);
            return;
          }
          
          // Clear any previous errors
          setSubmissionError(null);
          
          let url = `/submission-received/${result.id}`;
          
          // Build URL parameters properly
          const params = new URLSearchParams();
          
          if (sessionId) {
            params.append('m', sessionId);
          }
          
          if (game?.surveyUrl) {
            params.append('s', game.surveyUrl);
          }
          
          // Only add query string if we have parameters
          if (params.toString()) {
            url += `?${params.toString()}`;
          }

          navigate(url);
        }).catch((error) => {
          console.error("Error submitting game results:", error);
          
          // Capture in Sentry with context
          Sentry.captureException(error, {
            tags: {
              errorType: "submission_error",
              gameId: game?.id ?? gameId,
            },
            extra: {
              scoresLength: scores.length,
              gameName: game?.name,
              hasPollResults: !!pollResults,
              hasSessionId: !!sessionId,
            },
          });
          
          setSubmissionError("An error occurred while saving your submission. Please try again or contact support if the problem persists.");
          setIsSubmitting(false);
        });
      }
    };

    handleRoundSubmitted();
  }, [
    scores,
    game,
    currentRound,
    navigate,
    pollResults,
    rounds,
    userCompletedPolls,
    gameId,
    ipAddress,
    sessionId,
    isSubmitting,
    completedGamePlay,
    userAnsweredPreQuestions,
    userAnsweredPostQuestions,
    gameStartTimestamp,
  ]);

  // Draws a timeline indicating the current round within the game
  const Timeline = () => {
    if (rounds === 1) return <></>;
    var elements = [];
    for (var i = 0; i < rounds; i++) {
      elements.push(<Steps.Item key={i} />);
    }
    return (
      <Steps className="py-4" current={currentRound}>
        {elements}
      </Steps>
    );
  };

  // Text Heading indicating the game name and current round
  const HeadingText = () => {
    return (
      <h1 className="flex-d justify-content-center ">
        {`${name} `}
        <sup>
          <small>{progressText}</small>
        </sup>
      </h1>
    );
  };

  // Game view for the active round's mode
  const GameContentView = () => {
    if (currentRound >= rounds) {
      return <></>;
    }
    var params = {
      game: game,
      round: currentRound,
      config: game?.rounds[currentRound] ?? {},
      onSubmit: handleGamePlay,
    };

    switch (currentMode) {
      default: // "points"
        return <PointsGameView {...params} />;
      case "donut":
        return <DonutGameView {...params} />;
      case "sort":
        return <SortGameView {...params} />;
      case "realestate":
        return <Realestate {...params} />;
      case "question":
      case "pre-game-questions":
      case "post-game-questions":
        return <Questions {...params} />;
      case "info":
        return <InfoGameView {...params} />;
    }
  };

  const handleGamePlay = (data) => {
    // Add round timestamps to the round submission
    const roundData = {
      ...data,
      roundStartTimestamp: roundStartTimestamps[currentRound] || null,
      roundEndTimestamp: Timestamp(),
    };
    let newScores = [...scores, roundData];
    setScores(newScores);
  };

  const draftWarning = () => {
    if (game?.status === "draft") {
      return (
        <Alert variant="warning">
          <span className="alert-heading h4">
            <FontAwesomeIcon icon="fa-exclamation-triangle" />
            {" Draft Mode!"}
          </span>
          <p>
            This game is still in draft mode. Please be aware that the results may not be
            representative of the final game.
          </p>
        </Alert>
      );
    }
    return <></>;
  };

  // Content to draw while the game loads
  const Content = () => {
    return (
      <>
        {draftWarning()}
        {submissionError && (
          <Alert variant="danger" dismissible onClose={() => setSubmissionError(null)}>
            <span className="alert-heading h4">
              <FontAwesomeIcon icon="fa-exclamation-triangle" />
              {" Submission Error"}
            </span>
            <p>{submissionError}</p>
          </Alert>
        )}
        {!userAnsweredPreQuestions && (
          <Register
            title="Pre Game Questions"
            questions={game?.questions?.pre ?? {}}
            onSubmit={(value) => {
              setPollResults({ ...pollResults, ...{ pre: value } });
            }}
          />
        )}

        {userAnsweredPreQuestions && !completedGamePlay && (
          <>
            <HeadingText />
            <Timeline />
            <GameContentView />
          </>
        )}

        {userAnsweredPreQuestions && !userAnsweredPostQuestions && completedGamePlay && (
          <Register
            title="Post Game Questions"
            questions={game?.questions?.post ?? {}}
            onSubmit={(value) => {
              setPollResults({ ...pollResults, ...{ post: value } });
            }}
          />
        )}
      </>
    );
  };

  // Body to draw while loading
  return <>{isLoading ? <CircularProgress /> : <Content />}</>;
};

export default Play;
